import { useState } from 'react'
import axios from 'axios'

import { API_BASE_URL } from '../config/api'
import { artistKey, resolveLineup } from '../utils/scheduleUtils'

export const useEventArtists = (eventId, event, setEvent, user) => {
  const [artistEditModalOpen, setArtistEditModalOpen] = useState(false)
  const [editingArtistIndex, setEditingArtistIndex] = useState(null)
  const [error, setError] = useState('')

  const clearError = () => {
    setTimeout(() => setError(''), 5000)
  }

  const checkOwnership = () => {
    const eventOwnerId = event?.createdBy?._id?.toString()
    const currentUserId = user?.id?.toString()

    if (eventOwnerId !== currentUserId) {
      setError('You can only modify your own events')
      clearError()
      return false
    }
    return true
  }

  const updateArtists = async (updatedArtists) => {
    const response = await axios.put(`${API_BASE_URL}/api/events/${eventId}`, {
      artistsSeen: updatedArtists
    }, {
      withCredentials: true
    })
    setEvent(response.data)
  }

  /**
   * Puts artists on a day.
   *
   * This writes the SHARED lineup — `PUT /api/events/:id/lineup` — so the day
   * lands for everyone attending the event, not just this passport. That is
   * deliberate: which day an act plays is a fact about the festival, not about
   * one person's experience of it, and the schedule planner reads the same
   * list. The UI has to say so; a silent shared write is the failure mode
   * CLAUDE.md warns hardest about.
   *
   * Sends the whole lineup rather than a patch, matching saveSetTimes: the
   * server rebuilds officialLineup from the payload, which keeps sanitizing in
   * one place and avoids index arithmetic against a list a flyer update may
   * have reordered.
   *
   * @param {number[]} indexes positions in event.artistsSeen
   * @param {string}   dayKey  "YYYY-MM-DD", or '' to clear the day
   */
  const assignArtistsToDay = async (indexes, dayKey) => {
    if (!checkOwnership()) return false

    const targets = new Set(
      (indexes || [])
        .map(index => artistKey(event?.artistsSeen?.[index]?.name))
        .filter(Boolean)
    )
    if (targets.size === 0) return false

    const dayFor = (artist, fallback) =>
      targets.has(artistKey(artist.name)) ? (dayKey || null) : (fallback || null)

    const lineup = resolveLineup(event).map(artist => ({
      name: artist.name,
      genre: artist.genre || '',
      performanceDate: dayFor(artist, artist.performanceDate),
      startTime: artist.startTime || '',
      endTime: artist.endTime || '',
      stage: artist.stage || '',
      setTime: artist.setTime || ''
    }))

    // An artist can sit in artistsSeen without being on the shared lineup —
    // anyone can add to their own passport. Assigning a day to one of those
    // would otherwise write a lineup that simply does not mention them, and
    // the day would vanish with no error.
    const present = new Set(lineup.map(artist => artistKey(artist.name)))
    ;(event?.artistsSeen || []).forEach(artist => {
      const key = artistKey(artist.name)
      if (!key || present.has(key) || !targets.has(key)) return
      present.add(key)
      lineup.push({
        name: artist.name,
        genre: artist.genre || '',
        performanceDate: dayKey || null,
        startTime: artist.startTime || '',
        endTime: artist.endTime || '',
        stage: artist.stage || '',
        setTime: artist.setTime || ''
      })
    })

    try {
      const response = await axios.put(`${API_BASE_URL}/api/events/${eventId}/lineup`, {
        officialLineup: lineup
      }, { withCredentials: true })

      // Merge, don't replace: this endpoint answers with the shared lineup
      // only, and overwriting the event object would drop every personal field.
      setEvent(current => ({ ...current, officialLineup: response.data.officialLineup }))
      return true
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error assigning day:', err)
      setError(err.response?.data?.message || 'Failed to save the day')
      clearError()
      return false
    }
  }

  const openArtistEditModal = (index) => {
    setEditingArtistIndex(index)
    setArtistEditModalOpen(true)
  }

  const closeArtistModal = () => {
    setArtistEditModalOpen(false)
    setEditingArtistIndex(null)
  }

  const handleArtistSave = async (updatedArtist) => {
    if (editingArtistIndex !== null) {
      try {
        const updatedArtists = [...event.artistsSeen]
        updatedArtists[editingArtistIndex] = updatedArtist
        await updateArtists(updatedArtists)
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('Error updating artist:', err)
        setError('Failed to update artist details')
        clearError()
      }
    }
    setEditingArtistIndex(null)
  }

  const handleArtistToggle = async (artistIndex, field, newValue = null) => {
    if (!checkOwnership()) return

    try {
      let updatedArtists = [...event.artistsSeen]

      if (field === 'comment') {
        updatedArtists[artistIndex][field] = newValue
      } else if (field === 'remove') {
        updatedArtists = updatedArtists.filter((_, i) => i !== artistIndex)
      } else {
        updatedArtists[artistIndex][field] = !updatedArtists[artistIndex][field]
      }

      await updateArtists(updatedArtists)
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(`Error updating artist ${field}:`, err)

      if (err.response?.status === 403) {
        setError('Access denied: You can only modify your own events')
      } else {
        setError(`Failed to update artist ${field}`)
      }
      clearError()
    }
  }

  return {
    artistEditModalOpen,
    editingArtistIndex,
    error,
    openArtistEditModal,
    closeArtistModal,
    handleArtistSave,
    handleArtistToggle,
    assignArtistsToDay
  }
}